import { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { FormattedMessage, useIntl } from 'react-intl';
import { api } from '../../services/api-client';
import { useToast } from '../../contexts/ToastContext';

interface AddExerciseDialogProps {
  visible: boolean;
  onHide: () => void;
  /** Days that already exist in the cycle (filtered chips). */
  availableDays: number[];
  /** Groups per day, keyed by dayNumber, so the group dropdown is meaningful. */
  groupsByDay: Record<number, Array<{ groupNumber: number; name?: string | null }>>;
  defaultDay?: number;
  onSubmit: (params: {
    dayNumber: number;
    groupNumber: number;
    exerciseId: number;
    sets?: string;
    repetitions?: string;
    weight?: string;
  }) => Promise<boolean | void> | boolean | void;
}

const DAY_LABELS: Record<number, string> = {
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
  7: 'Domingo'
};

interface ExerciseOption {
  id: number;
  name: string;
}

const AddExerciseDialog: React.FC<AddExerciseDialogProps> = ({
  visible,
  onHide,
  availableDays,
  groupsByDay,
  defaultDay,
  onSubmit
}) => {
  const intl = useIntl();
  const { showToast } = useToast();

  const [day, setDay] = useState<number | null>(defaultDay ?? availableDays[0] ?? null);
  const [groupNumber, setGroupNumber] = useState<number | null>(null);
  const [exerciseId, setExerciseId] = useState<number | null>(null);
  const [sets, setSets] = useState('');
  const [reps, setReps] = useState('');
  const [weight, setWeight] = useState('');
  const [search, setSearch] = useState('');
  const [exercises, setExercises] = useState<ExerciseOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!visible) return;
    // Reset on open
    setDay(defaultDay ?? availableDays[0] ?? null);
    setGroupNumber(null);
    setExerciseId(null);
    setSets('');
    setReps('');
    setWeight('');
    setSearch('');
  }, [visible, defaultDay, availableDays]);

  // Load exercise catalog when dialog opens. Debounced search.
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      api.exercise
        .fetchCoachExercises({ page: 1, limit: 100, search: search || undefined })
        .then((res) => {
          if (cancelled) return;
          const list = (res?.data?.items ?? res?.data ?? []) as Array<{ id: number; name: string }>;
          setExercises(list.map((e) => ({ id: e.id, name: e.name })));
        })
        .catch(() => {
          if (!cancelled) setExercises([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [visible, search]);

  const dayOptions = useMemo(
    () => availableDays.map((d) => ({ label: DAY_LABELS[d] ?? `Día ${d}`, value: d })),
    [availableDays]
  );

  const groupOptions = useMemo(() => {
    if (day == null) return [];
    return (groupsByDay[day] ?? []).map((g) => ({
      label: g.name ? `${g.name} (G${g.groupNumber})` : `Grupo ${g.groupNumber}`,
      value: g.groupNumber
    }));
  }, [day, groupsByDay]);

  // Auto-pick first group when day changes
  useEffect(() => {
    const first = groupOptions[0];
    if (first && groupNumber == null) setGroupNumber(first.value);
  }, [groupOptions, groupNumber]);

  const canSubmit = day != null && groupNumber != null && exerciseId != null;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      // onSubmit can return false (or void) on backend error — when the hook
      // swallowed it and showed a toast. In that case keep the dialog open
      // so the user can correct and retry.
      const result = await onSubmit({
        dayNumber: day as number,
        groupNumber: groupNumber as number,
        exerciseId: exerciseId as number,
        sets: sets.trim() || undefined,
        repetitions: reps.trim() || undefined,
        weight: weight.trim() || undefined
      });
      if (result === false) return;
      showToast(
        'success',
        intl.formatMessage({ id: 'common.success', defaultMessage: 'Listo' }),
        intl.formatMessage({ id: 'excelView.addExercise.success', defaultMessage: 'Ejercicio agregado.' })
      );
      onHide();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      header={intl.formatMessage({ id: 'excelView.addExercise.title', defaultMessage: 'Agregar ejercicio' })}
      visible={visible}
      onHide={onHide}
      style={{ width: 'min(520px, 95vw)' }}
      draggable={false}
      resizable={false}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button label={intl.formatMessage({ id: 'common.cancel' })} className="p-button-text" onClick={onHide} />
          <Button
            label={intl.formatMessage({ id: 'common.add', defaultMessage: 'Agregar' })}
            icon="pi pi-plus"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            loading={submitting}
            style={{ background: 'var(--et-cta-bg)', borderColor: 'var(--et-cta-bg)', color: 'var(--et-cta-fg)' }}
          />
        </div>
      }
    >
      <div className="flex flex-column gap-3">
        <div className="grid">
          <div className="col-6">
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="excelView.addExercise.day" defaultMessage="Día" />
            </label>
            <Dropdown
              value={day}
              options={dayOptions}
              onChange={(e) => {
                setDay(e.value);
                setGroupNumber(null);
              }}
              className="w-full p-inputtext-sm"
            />
          </div>
          <div className="col-6">
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="excelView.addExercise.group" defaultMessage="Grupo" />
            </label>
            <Dropdown
              value={groupNumber}
              options={groupOptions}
              onChange={(e) => setGroupNumber(e.value)}
              className="w-full p-inputtext-sm"
              placeholder={
                groupOptions.length === 0
                  ? intl.formatMessage({
                      id: 'excelView.addExercise.noGroups',
                      defaultMessage: 'Agregá un grupo primero'
                    })
                  : undefined
              }
              disabled={groupOptions.length === 0}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-2">
            <FormattedMessage id="excelView.addExercise.exercise" defaultMessage="Ejercicio" />
          </label>
          <Dropdown
            value={exerciseId}
            // The catalog list comes pre-filtered by the backend (via the
            // `search` state debounced in the load effect). We INTENTIONALLY
            // do NOT pass `filter={true}` here — PrimeReact's built-in filter
            // is client-side only and would filter just the 30 items the BE
            // returned, hiding matches that exist further down the catalog.
            // Instead we expose the filter input ourselves via filterTemplate
            // and route every keystroke straight to the backend search.
            options={exercises.map((e) => ({ label: e.name, value: e.id }))}
            onChange={(e) => setExerciseId(e.value)}
            placeholder={
              loading
                ? intl.formatMessage({ id: 'common.loading', defaultMessage: 'Cargando…' })
                : intl.formatMessage({
                    id: 'excelView.addExercise.selectExercise',
                    defaultMessage: 'Seleccioná un ejercicio'
                  })
            }
            className="w-full p-inputtext-sm"
            disabled={loading && exercises.length === 0}
            filter
            filterBy="label"
            filterPlaceholder={intl.formatMessage({ id: 'common.search', defaultMessage: 'Buscar...' })}
            filterTemplate={() => (
              <InputText
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={intl.formatMessage({ id: 'common.search', defaultMessage: 'Buscar...' })}
                className="w-full p-inputtext-sm"
                autoFocus
              />
            )}
            emptyMessage={
              loading
                ? intl.formatMessage({ id: 'common.loading', defaultMessage: 'Cargando…' })
                : intl.formatMessage({
                    id: 'excelView.addExercise.noMatches',
                    defaultMessage: 'Sin resultados'
                  })
            }
          />
          {search && exercises.length >= 100 && (
            <small className="text-color-secondary block mt-1">
              <FormattedMessage
                id="excelView.addExercise.partialResults"
                defaultMessage="Mostrando los primeros 100 — afiná tu búsqueda."
              />
            </small>
          )}
        </div>

        <div className="grid">
          <div className="col-4">
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="session.sets" defaultMessage="Sets" />
            </label>
            <InputText
              value={sets}
              onChange={(e) => setSets(e.target.value)}
              className="w-full p-inputtext-sm"
              placeholder="3"
            />
          </div>
          <div className="col-4">
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="session.reps" defaultMessage="Reps" />
            </label>
            <InputText
              value={reps}
              onChange={(e) => setReps(e.target.value)}
              className="w-full p-inputtext-sm"
              placeholder="10"
            />
          </div>
          <div className="col-4">
            <label className="block text-sm font-semibold mb-2">
              <FormattedMessage id="session.weight" defaultMessage="Peso (kg)" />
            </label>
            <InputText
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="w-full p-inputtext-sm"
              placeholder="20"
            />
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default AddExerciseDialog;
